import React, { memo } from 'react';

import { token } from '@atlaskit/tokens';
import { AvatarContent } from '@atlaskit/avatar/avatar-content';
import Avatar from '@atlaskit/avatar/avatar';
import Image from '@atlaskit/image';
import FileIcon from '@atlaskit/icon/core/file';
import { css } from '@atlaskit/css';

import type { IconSize } from './templateIconTypes.ts';
import { TemplateLegacyIcon } from './TemplateLegacyIcon.tsx';
import { DefaultIcon as DefaultIconWrapper } from './TemplateIconStyles.tsx';

export type TemplateIconTemplate = {
	iconURL?: string | null | undefined;
	darkModeIconURL?: string | null | undefined;
	styleClass?: string | null | undefined;
	itemModuleCompleteKey?: string | null | undefined;
	blueprintModuleCompleteKey?: string | null | undefined;
	contentBlueprintId?: string | null | undefined;
};

type TemplateIconProps = {
	template: TemplateIconTemplate;
	isDisabled?: boolean;
	size?: IconSize;
};

type InjectedTemplateIconProps = TemplateIconProps;

// @todo CTE-166 finalize icons once they are implemented on the BE

const iconWrapperStyles = css({
	alignSelf: 'flex-start',
});

const imageWrapperStyles = css({
	display: 'flex',
	boxSizing: 'border-box',
	margin: 0,
	marginRight: token('space.050'),
	padding: 0,
	flexShrink: 0,
	flexGrow: 0,
	alignSelf: 'flex-start',
	boxShadow: 'initial',
});

const InjectedTemplateIcon = ({
	template,
	isDisabled = false,
	size = 'medium',
}: InjectedTemplateIconProps) => {
	if (template.iconURL) {
		return (
			<div css={iconWrapperStyles} data-testid="template-icon-avatar">
				<Avatar appearance="square" size={size} isDisabled={isDisabled}>
					<div css={imageWrapperStyles}>
						<AvatarContent>
							<Image alt="" src={template.iconURL || ''} srcDark={template.darkModeIconURL || ''} />
						</AvatarContent>
					</div>
				</Avatar>
			</div>
		);
	} else if (template.styleClass) {
		return <TemplateLegacyIcon size={size} template={template} />;
	} else {
		// Ideally, this code will never be used. However, if the BE response is missing an iconURL for some reason,
		// this will return an icon with a grey background color that looks like the icon for 3rd party templates.
		return (
			<DefaultIconWrapper size={size}>
				<FileIcon spacing="spacious" color={token('color.icon.inverse')} label="" />
			</DefaultIconWrapper>
		);
	}
};

// The avatar-item component, which actually renders TemplateIcon, passes a backgroundColor
// prop to the TemplateIcon. The TemplateIcon and InjectedTemplateIcon disregard this backgroundColor
// prop. Therefore, we can ignore the backgroundColor prop for memoization.
const areEqual = (prevProps: TemplateIconProps, nextProps: TemplateIconProps) => {
	return (
		prevProps.template === nextProps.template &&
		prevProps.isDisabled === nextProps.isDisabled &&
		prevProps.size === nextProps.size
	);
};

export const TemplateIcon = memo(InjectedTemplateIcon, areEqual);
